import { useState, useEffect } from "react";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { ArrowLeft, Baby, CalendarCheck, TrendingUp, Milestone, BarChart, Activity, Calendar, Heart, BrainCircuit, Dna, ScanFace } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useNavigate } from "react-router-dom";
import { GestationForm } from "@/components/gestation-tracking/form/GestationForm";
import { GestationInfo } from "@/components/gestation-tracking/info/GestationInfo";
import { GestationStats } from "@/components/gestation-tracking/stats/GestationStats";
import { BabyDevelopment } from "@/components/gestation-tracking/development/BabyDevelopment";
import { addDays, isValid } from "date-fns";
import { DailyTipsButton } from "@/components/daily-tips/DailyTipsButton";
import { Progress } from "@/components/ui/progress";
import { Card, CardContent } from "@/components/ui/card";

const MobileGestationTracking = () => {
  const { toast } = useToast();
  const navigate = useNavigate();
  const [pregnancyStartDate, setPregnancyStartDate] = useState<Date | undefined>();
  const [activeTab, setActiveTab] = useState("overview");

  const { data: gestationData, refetch, isLoading } = useQuery({
    queryKey: ["gestation-tracking"],
    queryFn: async () => {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) {
        toast({
          title: "Non authentifié",
          description: "Veuillez vous connecter pour accéder à vos données",
          variant: "destructive",
        });
        return null;
      }

      const { data, error } = await supabase
        .from("gestation_tracking")
        .select("*")
        .eq("user_id", user.id)
        .maybeSingle();

      if (error) {
        console.error('Error fetching gestation data:', error);
        toast({
          title: "Erreur",
          description: "Impossible de récupérer vos données",
          variant: "destructive",
        });
        throw error;
      }
      
      return data;
    },
  });

  useEffect(() => {
    if (gestationData?.pregnancy_start_date) {
      const date = new Date(gestationData.pregnancy_start_date);
      if (isValid(date)) {
        setPregnancyStartDate(date);
      } else {
        console.error('Invalid pregnancy start date:', gestationData.pregnancy_start_date);
      }
    }
  }, [gestationData]);

  const dueDate = pregnancyStartDate ? addDays(pregnancyStartDate, 280) : null;
  const currentWeek = pregnancyStartDate 
    ? Math.max(0, Math.min(40, Math.floor((new Date().getTime() - pregnancyStartDate.getTime()) / (1000 * 60 * 60 * 24 * 7))))
    : null;

  const pregnancyData = {
    pregnancyStartDate,
    dueDate,
    currentWeek,
    notes: gestationData?.notes,
    nextCheckupDate: gestationData?.next_checkup_date,
    lastCheckupDate: gestationData?.last_checkup_date
  };

  if (isLoading) {
    return (
      <div className="p-4 flex items-center justify-center h-screen">
        <div className="animate-pulse text-primary">Chargement...</div>
      </div>
    );
  }

  const particles = Array.from({ length: 12 }).map((_, i) => ({
    id: i,
    size: Math.random() * 8 + 2,
    top: `${Math.random() * 100}%`,
    left: `${Math.random() * 100}%`,
    delay: Math.random() * 5,
    duration: Math.random() * 6 + 5
  }));

  return (
    <div className="p-4 pb-20 relative">
      <div className="fixed inset-0 -z-10 gestation-background">
        {particles.map((particle) => (
          <div 
            key={particle.id} 
            className="floating-particle"
            style={{
              width: `${particle.size}px`,
              height: `${particle.size}px`,
              top: particle.top,
              left: particle.left,
              animationDelay: `${particle.delay}s`,
              animationDuration: `${particle.duration}s`
            }}
          />
        ))}
        
        <svg className="absolute bottom-0 left-0 w-full h-1/2 opacity-20" viewBox="0 0 100 50" preserveAspectRatio="none">
          <path 
            d="M0,50 C30,30 70,20 100,0" 
            fill="none" 
            stroke="rgba(255,182,193,0.7)" 
            strokeWidth="1"
            className="animate-pulse"
          />
        </svg>
      </div>

      <div className="flex items-center gap-2 mb-4">
        <Button variant="ghost" size="icon" onClick={() => navigate(-1)}>
          <ArrowLeft className="h-5 w-5" />
        </Button>
        <h1 className="text-xl font-bold flex items-center">
          Suivi de grossesse
          <Baby className="h-5 w-5 ml-2 text-purple-600 animate-pulse" />
        </h1>
      </div>
      
      {pregnancyStartDate && currentWeek !== null && dueDate && (
        <div className="mb-4 animate-fade-in" style={{ animationDelay: "0.1s" }}>
          <div className="grid grid-cols-2 gap-3 mb-3">
            <Card className="bg-gradient-to-br from-purple-50 to-purple-100 overflow-hidden border-transparent shadow-sm transition-transform hover:scale-105 duration-300">
              <CardContent className="p-3 flex flex-col items-center">
                <div className="bg-purple-500 text-white p-2 rounded-full mb-2">
                  <Baby className="h-5 w-5" />
                </div>
                <span className="text-xl font-bold text-purple-600">{currentWeek}</span>
                <span className="text-xs text-purple-700">Semaine actuelle</span>
              </CardContent>
            </Card>
            <Card className="bg-gradient-to-br from-pink-50 to-pink-100 overflow-hidden border-transparent shadow-sm transition-transform hover:scale-105 duration-300">
              <CardContent className="p-3 flex flex-col items-center">
                <div className="bg-pink-500 text-white p-2 rounded-full mb-2">
                  <CalendarCheck className="h-5 w-5" />
                </div>
                <span className="text-xl font-bold text-pink-600">{40 - currentWeek}</span>
                <span className="text-xs text-pink-700">Semaines restantes</span>
              </CardContent>
            </Card>
          </div>
          
          <Card className="bg-gradient-to-br from-blue-50 to-indigo-50 border-transparent shadow-sm animate-fade-in" style={{ animationDelay: "0.2s" }}>
            <CardContent className="p-3">
              <div className="flex justify-between items-center mb-2">
                <div className="flex items-center gap-2">
                  <div className="bg-blue-500 text-white p-1.5 rounded-full">
                    <TrendingUp className="h-4 w-4" />
                  </div>
                  <span className="text-sm font-medium text-blue-800">Progression</span>
                </div>
                <span className="text-lg font-bold text-blue-600">{Math.round((currentWeek / 40) * 100)}%</span>
              </div>
              <Progress value={(currentWeek / 40) * 100} className="h-2 mb-2">
                <div 
                  className="h-full bg-gradient-to-r from-blue-400 to-purple-500 rounded-full"
                  style={{ width: `${(currentWeek / 40) * 100}%` }}
                />
              </Progress>
              <div className="flex justify-between text-xs text-gray-500">
                <span>Début</span>
                <span>Accouchement</span>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      <div className="flex mb-4 overflow-x-auto pb-2 border-b">
        <Button 
          variant={activeTab === "overview" ? "default" : "ghost"} 
          className={`mr-2 flex-shrink-0 ${activeTab === "overview" ? "bg-purple-600 hover:bg-purple-700" : ""}`}
          onClick={() => setActiveTab("overview")}
        >
          Vue d'ensemble
        </Button>
        <Button 
          variant={activeTab === "form" ? "default" : "ghost"} 
          className={`mr-2 flex-shrink-0 ${activeTab === "form" ? "bg-purple-600 hover:bg-purple-700" : ""}`}
          onClick={() => setActiveTab("form")}
        >
          Informations
        </Button>
        <Button 
          variant={activeTab === "stats" ? "default" : "ghost"} 
          className={`mr-2 flex-shrink-0 ${activeTab === "stats" ? "bg-purple-600 hover:bg-purple-700" : ""}`}
          onClick={() => setActiveTab("stats")}
        >
          Statistiques
        </Button>
        <Button 
          variant={activeTab === "development" ? "default" : "ghost"} 
          className={`flex-shrink-0 ${activeTab === "development" ? "bg-purple-600 hover:bg-purple-700" : ""}`}
          onClick={() => setActiveTab("development")}
        >
          Développement
        </Button>
      </div>

      {pregnancyStartDate && currentWeek !== null && (
        <div className="flex justify-center my-4">
          <DailyTipsButton 
            dataToAnalyze={pregnancyData} 
            context="pregnancy"
          />
        </div>
      )}

      <div className="space-y-6 scroll-area-mobile">
        {activeTab === "overview" && pregnancyStartDate && dueDate && currentWeek && (
          <div className="animate-fade-in glass-morphism">
            <GestationInfo 
              pregnancyStartDate={pregnancyStartDate}
              dueDate={dueDate}
              currentWeek={currentWeek}
            />
          </div>
        )}

        {activeTab === "overview" && pregnancyStartDate && currentWeek !== null && (
          <div className="grid grid-cols-2 gap-3 animate-fade-in" style={{ animationDelay: "0.3s" }}>
            <Card className="bg-gradient-to-br from-indigo-50 to-indigo-100 border-transparent shadow-sm">
              <CardContent className="p-3 flex flex-col items-center">
                <div className="bg-indigo-500 text-white p-2 rounded-full mb-2">
                  <BrainCircuit className="h-5 w-5" />
                </div>
                <span className="text-lg font-bold text-indigo-600">Actif</span>
                <span className="text-xs text-indigo-700">Développement cérébral</span>
              </CardContent>
            </Card>
            <Card className="bg-gradient-to-br from-teal-50 to-teal-100 border-transparent shadow-sm">
              <CardContent className="p-3 flex flex-col items-center">
                <div className="bg-teal-500 text-white p-2 rounded-full mb-2">
                  <Dna className="h-5 w-5" />
                </div>
                <span className="text-lg font-bold text-teal-600">Complet</span>
                <span className="text-xs text-teal-700">ADN formé</span>
              </CardContent>
            </Card>
          </div>
        )}

        {activeTab === "form" && (
          <div className="animate-fade-in glass-morphism">
            <GestationForm 
              gestationData={gestationData}
              onSuccess={refetch}
              initialData={{
                pregnancyStartDate,
              }}
            />
          </div>
        )}

        {activeTab === "stats" && pregnancyStartDate && currentWeek && dueDate && (
          <div className="animate-fade-in glass-morphism">
            <GestationStats 
              pregnancyStartDate={pregnancyStartDate}
              currentWeek={currentWeek}
              dueDate={dueDate}
            />
          </div>
        )}

        {activeTab === "development" && currentWeek && (
          <div className="overflow-hidden rounded-lg glass-morphism animate-fade-in">
            <div className="p-4">
              <h3 className="font-semibold mb-2 flex items-center">
                <ScanFace className="h-5 w-5 mr-2 text-purple-500" />
                Développement du bébé
              </h3>
              <BabyDevelopment 
                currentWeek={currentWeek}
              />
            </div>
          </div>
        )}

        {!pregnancyStartDate && activeTab !== "form" && (
          <div className="text-center py-4 glass-morphism animate-fade-in">
            <p className="text-muted-foreground mb-4">
              Veuillez d'abord entrer les informations de votre grossesse
            </p>
            <Button 
              onClick={() => setActiveTab("form")}
              className="bg-purple-600 hover:bg-purple-700"
            >
              Entrer mes informations
            </Button>
          </div>
        )}
      </div>
    </div>
  );
};

export default MobileGestationTracking;
